import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import { TourThemeInlineEdit } from '@/components/tours';
import { useTourContext } from '@/pages/tours/useTourContext';
import { Badge, Button, EmptyTableState, formatDate, Loading, RichTextContent } from '@startupquickstart/frontend';
import {
  Building2,
  Clock,
  FileText,
  Footprints,
  Map,
  Palette,
  SignpostBig,
  Sparkles,
  Volume2,
  Zap,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GenerateConclusionAction,
  GenerateConclusionIcon,
  GenerateDescriptionAction,
  GenerateDescriptionIcon,
  GenerateVoiceoversAction,
  GenerateVoiceoversIcon,
  RecalculateDetailsAction,
  RecalculateDetailsIcon,
} from './actions';

type VoiceoverType = 'both' | 'description' | 'conclusion';

export function TourOverviewTab() {
  const { tour, isLoading } = useTourContext();
  const [generateVoiceoversModalOpen, setGenerateVoiceoversModalOpen] = useState(false);
  const [generateDescriptionModalOpen, setGenerateDescriptionModalOpen] = useState(false);
  const [generateConclusionModalOpen, setGenerateConclusionModalOpen] = useState(false);
  const [recalculateDetailsModalOpen, setRecalculateDetailsModalOpen] = useState(false);
  const [defaultVoiceoverType, setDefaultVoiceoverType] = useState<VoiceoverType>('both');

  const openVoiceoversDialog = useCallback((type: VoiceoverType) => {
    setDefaultVoiceoverType(type);
    setGenerateVoiceoversModalOpen(true);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!tour) return null;

  return (
    <div className="space-y-6">
      {/* AI Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => setGenerateDescriptionModalOpen(true)}
        >
          <GenerateDescriptionIcon className="h-4 w-4" />
          Generate Description
        </Button>
        <Button
          variant="outline"
          onClick={() => setGenerateConclusionModalOpen(true)}
        >
          <GenerateConclusionIcon className="h-4 w-4" />
          Generate Conclusion
        </Button>
        <Button
          variant="outline"
          onClick={() => openVoiceoversDialog('both')}
        >
          <GenerateVoiceoversIcon className="h-4 w-4" />
          Generate Voiceovers
        </Button>
        <Button
          variant="outline"
          onClick={() => setRecalculateDetailsModalOpen(true)}
        >
          <RecalculateDetailsIcon className="h-4 w-4" />
          Recalculate Details
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-6">
          {/* Description card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Description</h2>
              <p className="text-sm text-muted-foreground">Tour description and details</p>
            </div>
            <div className="p-6 space-y-6">
              {tour.tagline && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Tagline
                  </div>
                  <p className="text-sm italic text-foreground">{tour.tagline}</p>
                </div>
              )}

              {/* Voiceover above description */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5 shrink-0" />
                  Voiceover
                </div>
                {tour.voiceover ? (
                  <VoiceoverAudioPlayer
                    voiceover={tour.voiceover}
                    isInSync={tour.isDescriptionVoiceoverInSync ?? null}
                    outOfSyncMessage="Description has changed since voiceover was generated"
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
                    <p className="text-sm text-muted-foreground">No voiceover generated</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openVoiceoversDialog('description')}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </Button>
                  </div>
                )}
              </div>

              {/* Description text */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Content
                </div>
                {tour.description ? (
                  <RichTextContent content={tour.description} />
                ) : (
                  <EmptyTableState
                    icon={<FileText className="h-6 w-6" />}
                    title="No description"
                    description="Edit to add a description"
                  />
                )}
              </div>
            </div>
            <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>Created: {formatDate(tour.createdAt)}</span>
              <span>Updated: {formatDate(tour.updatedAt)}</span>
            </div>
          </div>

          {/* Conclusion card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Conclusion</h2>
              <p className="text-sm text-muted-foreground">Closing text shown at the end of the tour</p>
            </div>
            <div className="p-6 space-y-6">
              {/* Conclusion Voiceover */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5 shrink-0" />
                  Voiceover
                </div>
                {tour.conclusionVoiceover ? (
                  <VoiceoverAudioPlayer
                    voiceover={tour.conclusionVoiceover}
                    isInSync={tour.isConclusionVoiceoverInSync ?? null}
                    outOfSyncMessage="Conclusion has changed since voiceover was generated"
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
                    <p className="text-sm text-muted-foreground">No voiceover generated</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openVoiceoversDialog('conclusion')}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </Button>
                  </div>
                )}
              </div>

              {/* Conclusion text */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Content
                </div>
                {tour.conclusion ? (
                  <RichTextContent content={tour.conclusion} />
                ) : (
                  <EmptyTableState
                    icon={<FileText className="h-6 w-6" />}
                    title="No conclusion"
                    description="Edit to add a conclusion"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tour Details card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Tour Details</h2>
              <p className="text-sm text-muted-foreground">Duration, difficulty and logistics</p>
            </div>
            <div className="p-6">
              <div className="divide-y">
                <div className="py-3 first:pt-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    Duration
                  </div>
                  <div className="text-sm">
                    {tour.duration || <span className="text-muted-foreground italic">Not set</span>}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <SignpostBig className="h-3.5 w-3.5 shrink-0" />
                    Stops
                  </div>
                  <div className="text-sm">
                    {tour.stops != null ? tour.stops : <span className="text-muted-foreground italic">Not set</span>}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Zap className="h-3.5 w-3.5 shrink-0" />
                    Difficulty
                  </div>
                  <div className="text-sm">
                    {tour.difficulty ? (
                      <Badge variant="default">{tour.difficulty}</Badge>
                    ) : (
                      <span className="text-muted-foreground italic">Not set</span>
                    )}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Footprints className="h-3.5 w-3.5 shrink-0" />
                    Estimated Walking
                  </div>
                  <div className="text-sm">
                    {tour.estimatedWalking || <span className="text-muted-foreground italic">Not set</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Classification card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Classification</h2>
              <p className="text-sm text-muted-foreground">Tour type and location</p>
            </div>
            <div className="p-6">
              <div className="divide-y">
                <div className="py-3 first:pt-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Map className="h-3.5 w-3.5 shrink-0" />
                    Tour Type
                  </div>
                  <div className="text-sm">
                    {tour.tourType ? (
                      <Link className="text-primary hover:underline" to={`/tour-types/${tour.tourType.id}`}>
                        {tour.tourType.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">N/A</span>
                    )}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    Greater Area
                  </div>
                  <div className="text-sm">
                    {tour.greaterArea ? (
                      <Link className="text-primary hover:underline" to={`/greater-areas/${tour.greaterArea.id}`}>
                        {tour.greaterArea.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">N/A</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Theme card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Theme</h2>
              <p className="text-sm text-muted-foreground">Theme configuration for this tour</p>
            </div>
            <div className="p-6">
              <div className="divide-y">
                <div className="py-3 first:pt-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                    <Palette className="h-3.5 w-3.5 shrink-0" />
                    Default Theme
                  </div>
                  <div className="text-sm mt-1">
                    <TourThemeInlineEdit tour={tour} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Highlights card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Highlights</h2>
              <p className="text-sm text-muted-foreground">Key tour highlights</p>
            </div>
            <div className="p-6">
              {tour.highlights && tour.highlights.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tour.highlights.map((highlight, index) => (
                    <Badge key={index} variant="secondary">
                      <Sparkles className="h-3 w-3 mr-1" />
                      {highlight}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No highlights set</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <GenerateVoiceoversAction
        tourId={tour.id}
        open={generateVoiceoversModalOpen}
        onOpenChange={setGenerateVoiceoversModalOpen}
        defaultVoiceoverType={defaultVoiceoverType}
      />
      <GenerateDescriptionAction
        tourId={tour.id}
        open={generateDescriptionModalOpen}
        onOpenChange={setGenerateDescriptionModalOpen}
      />
      <GenerateConclusionAction
        tourId={tour.id}
        open={generateConclusionModalOpen}
        onOpenChange={setGenerateConclusionModalOpen}
      />
      <RecalculateDetailsAction
        tourId={tour.id}
        open={recalculateDetailsModalOpen}
        onOpenChange={setRecalculateDetailsModalOpen}
      />
    </div>
  );
}
